import React, { FC, useEffect, useState } from "react";
import { Keyboard, Pressable, View, useWindowDimensions, KeyboardAvoidingView, Platform, TouchableOpacity, BackHandler, Image } from "react-native";
import { Theme, ThemeConsumer } from "@rneui/themed";
import { useIsFocused } from "@react-navigation/native";
import { useFormik } from "formik";
import * as Yup from 'yup';
import AsyncStorage from "@react-native-async-storage/async-storage";

//redux
import { useAppNavigation } from "../../types/@react-navigation/navigation";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { forgotPinApi, registerUser } from "../../redux/slices/authSlice";

import { ZenButton, ZenInput, ZenStatusBar, ZenText, ZenToast } from "zen-core-elements";

type SignInType = {
    type: "Login" | "Register" | "forgotPin"
}
const SignIn: FC<SignInType> = ({ type }) => {
    //State variable to use useIsFocused hook to notify the page is focused or not
    const isFocused = useIsFocused()
    //variable to store windowsDimentions
    let { width } = useWindowDimensions()
    //variable to handle keyboard activity
    const [keyboardOpen, setKeyboardOpen] = useState(false)
    const navigation = useAppNavigation()
    const dispatch = useAppDispatch()
    const { registerUser: { value: registerResponse, loading: registerLoading }, forgotPin: { value: forgotPinApiResponse, loading: forgotPinApiLoading } } = useAppSelector((state) => state?.authSlice)
    const [emailValue, setEmailValue] = useState("")

    // initial useEffect when page is focused
    useEffect(() => {
        Keyboard.addListener("keyboardDidShow", () => {
            setKeyboardOpen(true);
        });
        Keyboard.addListener("keyboardDidHide", () => {
            setKeyboardOpen(false);
        });
    }, [])

    useEffect(() => {
        if (isFocused) {
            getEmailValue()
        }
    }, [isFocused])

    const getEmailValue = async () => {
        if (type == "Login")
            setEmailValue(await AsyncStorage?.getItem("Email") || "")
    }

    useEffect(() => {
        const onBackPress = BackHandler.addEventListener("hardwareBackPress", () => {
            if (type === "Login") {
                if (navigation?.canGoBack()) {
                    if (navigation?.getState()?.routes?.[0]?.name === "ChangePin" || navigation?.getState()?.routes?.[0]?.name === "UpdatePin") {
                        navigation?.getState()?.routes?.shift()
                    }
                    else
                        return false
                }
                return false;
            }
            return false;
        });

        // Cleanup listener on unmount
        return () => onBackPress.remove();
    }, [type, navigation?.getState()?.routes]);

    //Method for formik validation
    const validationSchema = Yup.object().shape({
        email: Yup.string().required("An email id is required")
            .label("email")
            .matches(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, "Email id is invalid")
    })

    //Formik function
    const formik = useFormik({
        initialValues: { email: emailValue },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: () => {
            Keyboard?.dismiss()
            if (type == "Login")
                appSignIn()
            else if (type == "forgotPin")
                forgotPin()
            else
                register()
        }
    })

    useEffect(() => {
        if (registerResponse) {
            const storeRegisterMail = async () => {
                await AsyncStorage?.setItem("registerMail", formik?.values?.email)
            }
            storeRegisterMail()
            console.log("Register succesfully", registerResponse)
        }
    }, [registerResponse])

    useEffect(() => {
        if (forgotPinApiResponse && formik?.values?.email) {
            console.log("Mail sended successfully", forgotPinApiResponse)
            ZenToast({
                type: "success",
                message: "Mail Sended successfully"
            })
            navigation?.navigate("SignIn")
        }
    }, [forgotPinApiResponse])

    const forgotPin = async () => {
        await dispatch(forgotPinApi({ email: formik?.values?.email }))
    }

    const register = async () => {
        await dispatch(registerUser({ email: formik?.values?.email }))
        await AsyncStorage?.setItem("registerMail", formik?.values?.email)
        navigation.navigate("SignUp")
    }

    const appSignIn = async () => {
        await AsyncStorage.setItem("Email", formik?.values?.email)
        navigation.navigate("LoginPin")
    }

    return (
        <ThemeConsumer>
            {({ theme }: { theme: Theme }) => (
                <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined} enabled style={theme?.signInStyle?.outerContainer} onStartShouldSetResponder={() => { Keyboard?.dismiss(); return false }}>
                    <ZenStatusBar bgColor={theme?.primary} />
                    <View style={{ flex: 1, flexDirection: width > 900 ? "row" : 'column-reverse' }} >
                        <View style={[theme?.signInStyle?.innerContainer, { flex: width > 900 ? 0.47 : width > 600 ? 0.6 : 0.65 }]}>
                            {(width < 600 && keyboardOpen) ? null : <ZenText content={type == "Login" ? "Login to your POS account" : type == "forgotPin" ? "Having trouble signing in?" : "Set up your POS account"} bold={true} size={theme?.headerFontSize} color={theme?.light} style={{ textAlign: "center", paddingVertical: width > 600 ? 20 : 0, bottom: width > 600 ? 30 : 50 }} />}
                            {(width < 600 && keyboardOpen) ? null : (type == "Register" || type == "forgotPin") && <ZenText content={type == "Register" ? "An email will be sent to you for account verification" : "We will send you a link for resetting your pin to your registered email"} size={theme?.badgeFontSize} color={theme?.lightBackgroundColor} style={{ textAlign: "center", paddingVertical: 5, bottom: width > 600 ? 50 : 50 }} numberOfLines={2} />}
                            <View style={{ width: width > 900 ? "80%" : width > 600 ? '60%' : "90%" }}>
                                <ZenInput
                                    label={"Email *"}
                                    labelStyle={theme?.signInStyle?.labelStyle}
                                    value={formik?.values?.email}
                                    autoCapitalize="none"
                                    keyboardType="email-address"
                                    onChangeText={formik?.handleChange('email')}
                                    onBlur={formik?.handleBlur('email')}
                                    // Web: Enter submits, like the button (desktop keyboards). Native keeps its keyboard behaviour.
                                    onSubmitEditing={Platform.OS === "web" ? () => { if (!registerLoading && !forgotPinApiLoading) formik?.handleSubmit() } : undefined}
                                    errorMessage={formik?.errors?.email && !formik?.isValid && formik?.touched.email ? formik?.errors?.email : ''}
                                    errorStyle={[{ fontSize: theme?.errorFontSize, fontFamily: theme?.bold, color: theme?.red, }, formik?.errors?.email && !formik?.isValid && formik?.touched.email && { marginTop: 7, marginBottom: 20, marginHorizontal: 3, paddingHorizontal: 10, paddingVertical: 6, backgroundColor: theme?.badgeInActive, width: "99%", borderRadius: 1 }]}

                                    type="outlined"
                                    containerStyle={{ paddingHorizontal: 15, }}
                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                    inputStyle={theme?.signInStyle?.inputStyle}
                                />
                                <ZenButton
                                    title={type == "Register" ? "Register" : type == "forgotPin" ? "Send Email" : "Next"}
                                    loading={registerLoading || forgotPinApiLoading}
                                    loadingProps={{ color: theme?.primary }}
                                    titleStyle={theme?.signInStyle?.buttonTitleStyle}
                                    buttonStyle={[theme?.signInStyle?.buttonStyle]}
                                    onPress={() => formik?.handleSubmit()}
                                />

                                {type == "Login" && <Pressable style={theme?.signInStyle?.forgotPinView} onPress={() => navigation?.navigate("ForgotPin")}>
                                    <ZenText content="Forgot your pin?" size={theme?.smallHeaderFontSize} color={theme?.light} />
                                </Pressable>}
                                {type == "forgotPin" ?
                                    <View style={{ rowGap: 10, top: 20 }}>
                                        <View style={[theme?.signInStyle?.signUpButton]}>
                                            <ZenText content={"If you are already one of us,"} size={theme?.smallHeaderFontSize} color={theme?.light} />
                                            <TouchableOpacity onPress={() => { navigation?.navigate("SignIn") }}>
                                                <ZenText content={"Sign in here"} size={theme?.smallerFontSize} color={theme?.light} style={{ paddingLeft: 5 }} />
                                            </TouchableOpacity>
                                        </View>
                                        {/* <View style={[theme?.signInStyle?.signUpButton]}>
                                            <ZenText content={"Still not one of us?"} size={theme?.smallHeaderFontSize} color={theme?.light} />
                                            <TouchableOpacity onPress={() => { navigation?.navigate("Register") }}>
                                                <ZenText content={"Sign up"} size={theme?.smallerFontSize} color={theme?.light} style={{ paddingLeft: 5 }} />
                                            </TouchableOpacity>
                                        </View> */}
                                    </View> :
                                    <View style={[theme?.signInStyle?.signUpButton, { paddingTop: (type == "Register" && width < 600) ? 30 : 0 }]}>
                                        {/* <ZenText content={type == "Register" ? "If you are already one of us," : "Still not one of us?"} size={theme?.smallHeaderFontSize} color={theme?.light} />
                                        <TouchableOpacity onPress={() => {
                                            if (type == "Register")
                                                navigation?.navigate("SignIn")
                                            else
                                                navigation?.navigate("Register")
                                        }}>
                                            <ZenText content={type == "Login" ? "Sign up" : "Sign in here"} size={theme?.smallerFontSize} color={theme?.light} style={{ paddingLeft: 5 }} />
                                        </TouchableOpacity> */}
                                    </View>}

                            </View>
                            {keyboardOpen ? null : <View style={[theme?.signInStyle?.policyView, { flexWrap: width > 600 ? "nowrap" : "wrap" }]}>
                                <ZenText content="By continuing, you agree to" size={width > 600 ? 12 : theme?.badgeFontSize} color={theme?.light} />
                                <Pressable>
                                    <ZenText content="Terms and conditions & Privacy policy" bold={true} size={width > 600 ? 14 : theme?.badgeFontSize} color={theme?.secondary} style={{ paddingLeft: 5 }} />
                                </Pressable>
                            </View>}
                        </View>
                        <View style={{ flex: width > 900 ? 0.53 : width > 600 ? 0.4 : 0.35, backgroundColor: theme?.light }}>
                            <View style={[{ height: "100%" }]}>
                                <Image
                                    source={require("../../assets/signIn.jpg")}
                                    style={{ width: "100%", height: "100%" }}
                                    resizeMode="cover"
                                />
                            </View>
                        </View>
                    </View>
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>
    )
}



export default SignIn